import type { CompetitionSummaryDto } from '@napaicp/schemas'
import {
  COMPETITION_ROLES,
  GLOBAL_ROLES,
  type CompetitionRole,
  type GlobalRole,
} from '@napaicp/shared'

/**
 * The single place that decides "where does this person belong". Used by the
 * `/` landing redirect and by the header competition switcher, so a judge
 * switching competitions and a judge signing in for the first time land the
 * same way — one role map, not two that can drift apart.
 *
 * Priority when an account holds more than one competition role (globally,
 * or within the one competition a switch targets): competition_admin/staff
 * (they administer something) outrank judge (active reviewing work) outrank
 * student/teacher (participant view) outrank observer (read-only, no
 * dedicated screen yet). This is a judgment call, not a derived fact — it is
 * centralized here precisely so it is made once and documented once.
 */
const ROLE_PRIORITY: readonly CompetitionRole[] = [
  COMPETITION_ROLES.COMPETITION_ADMIN,
  COMPETITION_ROLES.STAFF,
  COMPETITION_ROLES.JUDGE,
  COMPETITION_ROLES.STUDENT,
  COMPETITION_ROLES.TEACHER,
  COMPETITION_ROLES.OBSERVER,
]

/** Roles that may render the `/admin/competitions/:id/*` workspace. */
export const ADMIN_WORKSPACE_ROLES: readonly CompetitionRole[] = [
  COMPETITION_ROLES.COMPETITION_ADMIN,
  COMPETITION_ROLES.STAFF,
]

/** The role-neutral list — every signed-in account can land here safely. */
export const DEFAULT_HOME = '/admin/competitions'

export function canAccessAdminWorkspace(
  globalRole: GlobalRole,
  competitionRoles: readonly CompetitionRole[],
): boolean {
  if (globalRole === GLOBAL_ROLES.SUPER_ADMIN) {
    return true
  }
  return competitionRoles.some((role) => ADMIN_WORKSPACE_ROLES.includes(role))
}

/**
 * The distinct screens a competition role can put someone on. Several
 * `CompetitionRole`s share a screen (`competition_admin`/`staff` both mean
 * "admin workspace", `student`/`teacher` both mean "student portal") — this
 * is that grouping, and the thing the in-header role switcher lets an
 * account toggle between when it holds roles that map to more than one of
 * these on the same competition.
 */
export type RoleView = 'admin' | 'judge' | 'student' | 'observer'

/** Every view in switcher display order, admin first — mirrors ROLE_PRIORITY. */
export const ROLE_VIEW_ORDER: readonly RoleView[] = ['admin', 'judge', 'student', 'observer']

const VIEW_FOR_ROLE: Record<CompetitionRole, RoleView> = {
  [COMPETITION_ROLES.COMPETITION_ADMIN]: 'admin',
  [COMPETITION_ROLES.STAFF]: 'admin',
  [COMPETITION_ROLES.JUDGE]: 'judge',
  [COMPETITION_ROLES.STUDENT]: 'student',
  [COMPETITION_ROLES.TEACHER]: 'student',
  [COMPETITION_ROLES.OBSERVER]: 'observer',
}

/** Where `view` on `competitionId` should navigate to. */
export function pathForRoleView(view: RoleView, competitionId: string): string {
  switch (view) {
    case 'admin':
      return `/admin/competitions/${competitionId}/overview`
    case 'judge':
      return `/judge/assignments?competitionId=${competitionId}`
    case 'student':
      return `/student/competitions/${competitionId}`
    case 'observer':
      return DEFAULT_HOME
  }
}

/** Where holding `role` on `competitionId` should navigate to. */
function homeForCompetitionRole(role: CompetitionRole, competitionId: string): string {
  return pathForRoleView(VIEW_FOR_ROLE[role], competitionId)
}

/**
 * Every distinct screen the account can reach on `competitionId`, in display
 * order — the set the header's role switcher renders as options. A super
 * admin always has the admin view available, on top of whatever competition
 * roles it separately holds there (a super admin can also be a judge).
 */
export function availableRoleViews(
  globalRole: GlobalRole,
  competitionRoles: readonly CompetitionRole[],
  competitionId: string,
): { view: RoleView; path: string }[] {
  const views = new Set<RoleView>()
  if (globalRole === GLOBAL_ROLES.SUPER_ADMIN) {
    views.add('admin')
  }
  for (const role of competitionRoles) {
    views.add(VIEW_FOR_ROLE[role])
  }
  return ROLE_VIEW_ORDER.filter((view) => views.has(view)).map((view) => ({
    view,
    path: pathForRoleView(view, competitionId),
  }))
}

/**
 * Used by the competition switcher: given the roles the caller holds on the
 * competition they just picked, where should the switch land them. Never
 * `/admin/*` for someone who does not administer that competition.
 */
export function homeForCompetition(
  globalRole: GlobalRole,
  competitionRoles: readonly CompetitionRole[],
  competitionId: string,
): string {
  if (globalRole === GLOBAL_ROLES.SUPER_ADMIN) {
    return `/admin/competitions/${competitionId}/overview`
  }
  for (const role of ROLE_PRIORITY) {
    if (competitionRoles.includes(role)) {
      return homeForCompetitionRole(role, competitionId)
    }
  }
  return DEFAULT_HOME
}

/**
 * Used by the `/` landing redirect: given every competition the caller
 * belongs to (each with its own role list), where their session should open.
 * Super admins land on the role-neutral competitions list, which already
 * renders correctly empty. An account that holds no competition role at all
 * — a freshly created student/teacher account that has not registered for
 * anything yet — is sent to `/home` instead: `/admin/competitions` is an
 * admin-labeled page that would render permanently empty for them with no
 * path back to the public competition list they need to browse and
 * self-register from (see production QA finding: new accounts couldn't find
 * their way to a competition after signing in).
 */
export function resolveSignedInHome(
  globalRole: GlobalRole,
  competitions: readonly Pick<CompetitionSummaryDto, 'roles'>[],
): string {
  if (globalRole === GLOBAL_ROLES.SUPER_ADMIN) {
    return DEFAULT_HOME
  }
  const heldRoles = new Set(competitions.flatMap((competition) => competition.roles))
  for (const role of ROLE_PRIORITY) {
    if (heldRoles.has(role)) {
      switch (role) {
        case COMPETITION_ROLES.COMPETITION_ADMIN:
        case COMPETITION_ROLES.STAFF:
          return DEFAULT_HOME
        case COMPETITION_ROLES.JUDGE:
          return '/judge/assignments'
        case COMPETITION_ROLES.STUDENT:
        case COMPETITION_ROLES.TEACHER:
          return '/student'
        case COMPETITION_ROLES.OBSERVER:
          return DEFAULT_HOME
      }
    }
  }
  return heldRoles.size === 0 ? '/home' : DEFAULT_HOME
}
